import { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useMinuteNow } from "@bw/shared/hooks";
import type {
  PartyResponse,
  QueueEntryResponse,
} from "@bw/shared/types/generated";
import { formatDropInSessionExpiry } from "@bw/shared/utils";
import { Page } from "../../../common/components/Page";
import { useQueueEntry } from "../hooks/useQueueEntry";
import { useQueueStats } from "../hooks/useQueueStats";
import { useLeaveQueue } from "../hooks/useLeaveQueue";
import { useFacilityMode } from "../hooks/useFacilityMode";
import { QueuePositionCard } from "../components/QueuePositionCard";
import { QueueBusynessCard } from "../components/QueueBusynessCard";
import { LeaveQueueSheet } from "../components/LeaveQueueSheet";
import { QueueAssignedPage } from "./QueueAssignedPage";
import { isTerminalQueueEntryStatus } from "../lib/queueEntryStatus";
import { useDropInCourtState } from "../hooks/useDropInCourtState";
import { useDropInSession } from "../hooks/useDropInSession";
import { useMyParty } from "../hooks/useMyParty";
import {
  QueueGroupSummary,
  type QueueGroupState,
} from "../components/QueueGroupSummary";
import {
  activeDropInAuthorizationExpiresAt,
  dropInSessionSnapshotIsAuthoritative,
} from "../lib/activeDropInAuthorization";

function isPartyForWaitingEntry(
  party: PartyResponse,
  entry: QueueEntryResponse,
): boolean {
  const members = party.eligibility.members ?? [];
  const currentUsers = members.filter((member) => member.is_current_user);
  const leaders = members.filter((member) => member.is_leader);
  const viewerMatchesRoster =
    currentUsers.length === 1 &&
    leaders.length === 1 &&
    (party.viewer.role === "leader"
      ? currentUsers[0] === leaders[0]
      : party.viewer.role === "member" && currentUsers[0] !== leaders[0]);

  return (
    entry.status === "waiting" &&
    party.status === "queued" &&
    party.entry_id === entry.entry_id &&
    party.facility_id === entry.facility_id &&
    party.party_id === entry.group_id &&
    party.eligibility.party_id === party.party_id &&
    party.eligibility.facility_id === party.facility_id &&
    party.capacity.accepted === members.length &&
    viewerMatchesRoster
  );
}

/**
 * Queue-WAITING view (/dropin/:facilityId/queue/:entryId).
 *
 * Reconciles the entry from facility court-state events, overlays facility
 * busyness (queue length + courts open — never "spots left"), and offers a
 * confirmed Leave. When an event refresh flips status to "assigned" it yields to the
 * "you're up" surface (QueueAssignedPage) in place — same route, no navigation.
 *
 * Auto policy deadlines are shown directly when supplied by the server. A
 * fallback estimate remains approximate; a null estimate reads "wait —".
 */
export function QueueWaitingPage() {
  const { facilityId = "", entryId = "" } = useParams<{
    facilityId: string;
    entryId: string;
  }>();
  const navigate = useNavigate();
  const now = useMinuteNow();
  const sessionQuery = useDropInSession(facilityId, {
    refreshMode: "expiry",
  });
  const session = sessionQuery.data ?? null;
  const activeAuthorizationExpiresAt = activeDropInAuthorizationExpiresAt(
    session,
    facilityId,
  );
  const sessionAuthorizationIsFresh =
    dropInSessionSnapshotIsAuthoritative(sessionQuery) &&
    activeAuthorizationExpiresAt !== null;
  const { isActorRevisionPending } = useDropInCourtState(facilityId, 0, {
    enabled: sessionAuthorizationIsFresh,
    authorizationExpiresAt: sessionAuthorizationIsFresh
      ? activeAuthorizationExpiresAt
      : null,
  });

  const entryQuery = useQueueEntry(entryId);
  const stats = useQueueStats(facilityId, Boolean(facilityId));
  const leave = useLeaveQueue(entryId, facilityId);
  // C3: mode-specific guidance renders only after the facility mode is known.
  const facilityMode = useFacilityMode(facilityId);
  const partyQuery = useMyParty(facilityId);
  const queueAssignmentMode = facilityMode.data?.queue_assignment_mode ?? null;
  const assignmentModeUnavailable = facilityMode.isError;
  const sessionExpiry = formatDropInSessionExpiry(session?.expires_at, {
    now,
    timeZone: facilityMode.data?.timezone,
  });

  const [confirming, setConfirming] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const entry = entryQuery.data ?? null;
  const entryMatchesRoute =
    entry?.entry_id === entryId && entry.facility_id === facilityId;
  const entrySnapshotSettled =
    entryQuery.isSuccess &&
    !entryQuery.isFetching &&
    !entryQuery.isError &&
    entryMatchesRoute;
  const entrySnapshotCurrent = entrySnapshotSettled && !isActorRevisionPending;
  const waitingEntry =
    entrySnapshotSettled && entry?.status === "waiting" ? entry : null;
  const entrySnapshotRejected =
    entryQuery.isError ||
    (entryQuery.isSuccess &&
      !entryQuery.isFetching &&
      !isActorRevisionPending &&
      !entryMatchesRoute);
  const party = partyQuery.data ?? null;
  const partySnapshotCurrent =
    entrySnapshotCurrent &&
    partyQuery.isSuccess &&
    partyQuery.isFetchedAfterMount &&
    !partyQuery.isFetching &&
    !partyQuery.isError;
  const partyMatchesEntry =
    partySnapshotCurrent &&
    party !== null &&
    waitingEntry !== null &&
    isPartyForWaitingEntry(party, waitingEntry);
  let queueGroupState: QueueGroupState;
  if (partyQuery.isError && !partyQuery.isFetching) {
    queueGroupState = { kind: "error" };
  } else if (!partySnapshotCurrent) {
    queueGroupState = { kind: "loading" };
  } else if (party === null) {
    queueGroupState = { kind: "solo" };
  } else if (partyMatchesEntry) {
    queueGroupState = { kind: "party", party };
  } else {
    queueGroupState = { kind: "mismatch" };
  }
  const canLeaveQueue =
    sessionAuthorizationIsFresh &&
    partySnapshotCurrent &&
    (party === null || (partyMatchesEntry && party.viewer.role === "leader"));
  const statsSnapshotCurrent =
    entrySnapshotCurrent &&
    stats.isSuccess &&
    stats.isFetchedAfterMount &&
    !stats.isFetching &&
    !stats.isError &&
    stats.data?.facility_id === facilityId &&
    stats.data.facility_id === entry?.facility_id;

  // Status flip → hand off to the assigned surface (same route).
  if (entryMatchesRoute && entry?.status === "assigned") {
    return (
      <QueueAssignedPage
        sessionExpiresAt={sessionQuery.data?.expires_at}
        sessionExpiryLabel={sessionExpiry}
      />
    );
  }

  // Terminal (completed/expired/cancelled, party-return-after-game fix): the
  // entry is dead — a naturally-elapsed block, a staff clear/TTL, or a
  // dup-join cancellation (#1522). The OLD waiting card would render with a
  // blank position (nothing to show, nowhere to go); route back to the
  // session hub instead, which now surfaces the reopened party for a
  // one-tap re-queue (or the join CTA). The SR-only alert preserves an
  // accessible state-change announcement even though nothing else renders
  // here — mirrors the FOCUS-ORPHAN-1 guarantee the old in-page terminal
  // screen gave, without reintroducing the dead-end view itself.
  if (entryMatchesRoute && entry && isTerminalQueueEntryStatus(entry.status)) {
    return (
      <>
        <Box
          role="alert"
          sx={{
            position: "absolute",
            width: "1px",
            height: "1px",
            p: 0,
            m: "-1px",
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          Your spot ended — taking you back to your session.
        </Box>
        <Navigate to={`/dropin/${facilityId}/session`} replace />
      </>
    );
  }

  function handleConfirmLeave() {
    if (
      !entrySnapshotCurrent ||
      !canLeaveQueue ||
      activeDropInAuthorizationExpiresAt(session, facilityId) === null
    ) {
      return;
    }
    setLeaveError(null);
    leave.mutate(undefined, {
      onSuccess: () => navigate(`/dropin/${facilityId}/session`),
      onError: () =>
        setLeaveError(
          "We couldn't take you out of the queue. Please try again.",
        ),
    });
  }

  return (
    <Page>
      <Stack
        spacing={3}
        sx={{ "& .MuiButton-root.Mui-disabled": { pointerEvents: "auto" } }}
      >
        <Box>
          <Typography component="h1" variant="h4">
            You&apos;re in line
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {assignmentModeUnavailable
              ? "Court assignment details are unavailable."
              : queueAssignmentMode === null
                ? "Checking how courts are assigned."
                : queueAssignmentMode === "manual"
                  ? "Staff will call your group when a court opens."
                  : "Hang nearby. We'll move you up as courts free up."}
          </Typography>
        </Box>

        {!waitingEntry ? (
          entrySnapshotRejected ? (
            <Alert severity="error">
              We couldn&apos;t load your queue status. Please try again.
            </Alert>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              role="status"
              aria-live="polite"
              sx={{ alignItems: "center" }}
            >
              <CircularProgress size={18} />
              <Typography color="text.secondary">
                Loading your place in line…
              </Typography>
            </Stack>
          )
        ) : (
          <Stack spacing={2}>
            <QueuePositionCard
              entry={waitingEntry}
              queueAssignmentMode={queueAssignmentMode}
              assignmentModeUnavailable={assignmentModeUnavailable}
            />

            <QueueGroupSummary
              entry={waitingEntry}
              state={queueGroupState}
              timeZone={facilityMode.data?.timezone}
              onRetry={() => {
                void Promise.all([entryQuery.refetch(), partyQuery.refetch()]);
              }}
            />

            {sessionExpiry ? (
              <Typography variant="body2" color="text.secondary">
                <time dateTime={sessionQuery.data?.expires_at ?? undefined}>
                  {sessionExpiry}
                </time>
              </Typography>
            ) : null}

            {statsSnapshotCurrent && stats.data ? (
              <QueueBusynessCard stats={stats.data} />
            ) : null}

            {canLeaveQueue ? (
              <Box
                sx={{ position: "sticky", bottom: 16, alignSelf: "flex-start" }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={() => setConfirming(true)}
                >
                  Leave the queue
                </Button>
              </Box>
            ) : null}
          </Stack>
        )}

        {confirming ? (
          <LeaveQueueSheet
            leaving={leave.isPending}
            canLeave={entrySnapshotCurrent && canLeaveQueue}
            error={leaveError}
            onCancel={() => {
              if (leave.isPending) return;
              setConfirming(false);
              setLeaveError(null);
            }}
            onLeave={handleConfirmLeave}
          />
        ) : null}
      </Stack>
    </Page>
  );
}
